/** @file jointMeshes: the bundle's glTF meshes on their joints, following the pose, toon shading on or off. */
import type { Material } from '@babylonjs/core/Materials/material.js';
import { Quaternion } from '@babylonjs/core/Maths/math.vector.js';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh.js';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { MeshDrawable } from '@zilo/pose3d-format/types/drawable';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { PoseBuffer } from '@zilo/pose3d-pose/contract';
import { POSE_FLAGS } from '@zilo/pose3d-pose/layout/poseFlags';
import { nodeIndexOf } from '@zilo/pose3d-render-core/camera/nodeIndexOf';
import { zScaleViewOf } from '@zilo/pose3d-render-core/camera/zScaleViewOf';
import { poseNodeOf } from '@zilo/pose3d-render-core/place/poseNodeOf';
import type { LoadedBundle } from '@zilo/pose3d-runtime-babylon/bundle/loadedBundle';
import { entityRoot } from '@zilo/pose3d-runtime-babylon/mesh/entityRoot';
import { loadGltfMesh } from '@zilo/pose3d-runtime-babylon/mesh/loadGltfMesh';
import { nodeTransform } from '@zilo/pose3d-runtime-babylon/mesh/nodeTransform';
import { placeEntityRoot } from '@zilo/pose3d-runtime-babylon/mesh/placeEntityRoot';
import { toonMaterial } from '@zilo/pose3d-runtime-babylon/mesh/toonMaterial';

/** The joint meshes of one instance: one root per mesh drawable (by id) under the entity root. */
export type JointMeshes = {
  roots: ReadonlyMap<string, TransformNode>;
  setToon(on: boolean): void;
  setVisible(on: boolean): void;
  sync(pose: PoseBuffer, entityPos: Vec3): void;
  dispose(): void;
};

type Joint = { drawable: MeshDrawable; node: TransformNode; nodeIndex: number; meshes: AbstractMesh[] };

/** Loads the drawable's GLB under a new joint node (glTF roots re-parented to it). */
async function loadJoint(scene: Scene, root: TransformNode, bundle: LoadedBundle, d: MeshDrawable, nodeIndex: number): Promise<Joint | null> {
  const bytes = bundle.meshes[d.mesh];
  if (bytes === undefined) return null;
  const node = new TransformNode(d.id, scene);
  node.parent = root;
  node.rotationQuaternion = new Quaternion();
  const meshes = await loadGltfMesh(scene, bytes, d.id);
  for (const m of meshes) if (m.parent === null) m.parent = node;
  return { drawable: d, node, nodeIndex, meshes };
}

/**
 * One `TransformNode` per mesh drawable whose mesh the bundle holds (`meshes/<id>.glb`), under an entity root that
 * `sync` places at the entity (z scaled by zScaleView) and whose children follow `nodeTransform`; a joint whose node
 * is hidden in the pose is disabled. `setToon(true)` swaps each loaded material for a `toonMaterial` (drawables with
 * `toon: false` keep theirs), `setToon(false)` restores the loader's material instances.
 */
export async function jointMeshes(scene: Scene, bundle: LoadedBundle, puppet: Puppet, toon: boolean): Promise<JointMeshes> {
  const root = entityRoot(scene, `${puppet.name}/meshes`);
  const index = nodeIndexOf(puppet);
  const drawables = puppet.drawables.filter((d): d is MeshDrawable => d.kind === 'mesh');
  const joints = (await Promise.all(drawables.map((d) => loadJoint(scene, root, bundle, d, index.get(d.node) ?? -1))))
    .filter((j): j is Joint => j !== null);
  const original = new Map<AbstractMesh, Material | null>(joints.flatMap((j) => j.meshes.map((m) => [m, m.material] as const)));
  const toons = new Map<Material, Material>();
  const toonOf = (m: Material): Material => toons.get(m) ?? toons.set(m, toonMaterial(scene, m, `${m.name}/toon`)).get(m) ?? m;
  const setToon = (on: boolean): void => {
    for (const j of joints) {
      for (const m of j.meshes) {
        const base = original.get(m) ?? null;
        m.material = on && j.drawable.toon !== false && base !== null ? toonOf(base) : base;
      }
    }
  };
  setToon(toon);
  const zs = zScaleViewOf(bundle.profile);
  return {
    roots: new Map(joints.map((j) => [j.drawable.id, j.node])),
    setToon,
    setVisible: (on) => { root.setEnabled(on); },
    sync: (pose, entityPos) => {
      placeEntityRoot(root, entityPos, zs);
      for (const j of joints) {
        if (j.nodeIndex < 0) continue;
        nodeTransform(pose, j.nodeIndex, j.drawable.offset, bundle.profile, { position: j.node.position, rotation: j.node.rotationQuaternion ?? new Quaternion() });
        j.node.setEnabled((poseNodeOf(pose, j.nodeIndex).flags & POSE_FLAGS.hidden) === 0);
      }
    },
    dispose: () => {
      root.dispose();
      for (const t of toons.values()) t.dispose();
    },
  };
}
